import type { MediaItem } from "@/lib/metadata";
import type { DiveSample } from "@/lib/shearwater-parser";

interface MediaTimelineProps {
  media: MediaItem[];
  diveDuration: number; // in seconds
  samples?: DiveSample[];
  onPhotoClick?: (photo: MediaItem) => void;
}

function formatTimestamp(seconds: number, diveDuration: number): string {
  if (seconds < 0) {
    const mins = Math.ceil(Math.abs(seconds) / 60);
    return mins === 1 ? "1 min before" : `${mins} min before`;
  }
  if (seconds > diveDuration) {
    const mins = Math.floor((seconds - diveDuration) / 60);
    return mins === 0 ? "After dive" : mins === 1 ? "1 min after" : `${mins} min after`;
  }
  const mins = Math.floor(seconds / 60);
  return `${mins}min`;
}

function getDepthAtTime(seconds: number, samples: DiveSample[]): number | null {
  if (seconds < 0 || samples.length === 0) return null;

  // Find the closest sample to this timestamp
  let closest = samples[0];
  let minDiff = Math.abs(samples[0].elapsedSeconds - seconds);

  for (const sample of samples) {
    const diff = Math.abs(sample.elapsedSeconds - seconds);
    if (diff < minDiff) {
      minDiff = diff;
      closest = sample;
    }
    // Since samples are in order, we can break once we start getting further away
    if (sample.elapsedSeconds > seconds && diff > minDiff) break;
  }

  return closest.depthFeet;
}

type PhotoSection = "before" | "during" | "after";

function getSection(timestamp: number, diveDuration: number): PhotoSection {
  if (timestamp < 0) return "before";
  if (timestamp > diveDuration) return "after";
  return "during";
}

interface TimelineItemProps {
  item: MediaItem;
  diveDuration: number;
  samples?: DiveSample[];
  onPhotoClick?: (photo: MediaItem) => void;
  dotColor?: string;
}

function TimelineItem({ item, diveDuration, samples, onPhotoClick, dotColor = "bg-blue-500" }: TimelineItemProps) {
  const depth = samples ? getDepthAtTime(item.timestamp, samples) : null;
  const isDuringDive = item.timestamp >= 0 && item.timestamp <= diveDuration;

  return (
    <div className="relative">
      {/* Dot + Timestamp row */}
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-3 h-3 rounded-full ${dotColor} border-2 border-slate-800 shrink-0 z-10`} />
        <span className="text-xs text-slate-400">
          {formatTimestamp(item.timestamp, diveDuration)}
          {isDuringDive && depth !== null && ` (${Math.round(depth)}ft)`}
        </span>
      </div>

      {/* Photo - indented to align with timestamp */}
      <div className="pl-6">
        <button
          onClick={() => onPhotoClick?.(item)}
          className="block w-full max-w-none rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all cursor-pointer"
        >
          {item.type === "video" ? (
            <video
              src={item.thumbnail || item.filename}
              className="w-full h-auto object-cover"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
            />
          ) : (
            <img
              src={item.thumbnail || item.filename}
              alt={item.caption || "Dive photo"}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          )}
        </button>

        {/* Caption */}
        {item.caption && (
          <p className="text-sm text-slate-300 mt-2">{item.caption}</p>
        )}
      </div>
    </div>
  );
}

export function MediaTimeline({ media, diveDuration, samples, onPhotoClick }: MediaTimelineProps) {
  if (!media || media.length === 0) {
    return null;
  }

  // Group photos by section
  const beforeDive = media.filter(m => getSection(m.timestamp, diveDuration) === "before");
  const duringDive = media.filter(m => getSection(m.timestamp, diveDuration) === "during");
  const afterDive = media.filter(m => getSection(m.timestamp, diveDuration) === "after");

  return (
    <div className="relative my-4">
      {/* Vertical line - positioned to align with dot centers */}
      <div className="absolute left-[5px] -top-2 -bottom-2 w-0.5 bg-slate-600" />
      {/* Terminal dots */}
      <div className="absolute left-[5px] -top-2 w-0.5 h-0.5 rounded-full bg-slate-600" />
      <div className="absolute left-[5px] -bottom-2 w-0.5 h-0.5 rounded-full bg-slate-600" />

      <div className="space-y-6">
        {/* Before dive section */}
        {beforeDive.length > 0 && (
          <>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-3 h-3 rounded-full bg-slate-500 border-2 border-slate-800 shrink-0 z-10" />
              <span className="text-xs text-slate-400 font-medium">Before dive</span>
            </div>
            {beforeDive.map((item, index) => (
              <div key={`before-${index}`} className="relative">
                {/* Just show the media without timestamp for before-dive items */}
                <div className="pl-6">
                  <button
                    onClick={() => onPhotoClick?.(item)}
                    className="block w-full max-w-none rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all cursor-pointer"
                  >
                    {item.type === "video" ? (
                      <video
                        src={item.thumbnail || item.filename}
                        className="w-full h-auto object-cover"
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      />
                    ) : (
                      <img
                        src={item.thumbnail || item.filename}
                        alt={item.caption || "Dive photo"}
                        className="w-full h-auto object-cover"
                        loading="lazy"
                      />
                    )}
                  </button>
                  {item.caption && (
                    <p className="text-sm text-slate-300 mt-2">{item.caption}</p>
                  )}
                </div>
              </div>
            ))}
            {/* Dive start marker */}
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-green-500 border-2 border-slate-800 shrink-0 z-10" />
              <span className="text-xs text-green-400 font-medium">Dive start</span>
            </div>
          </>
        )}

        {/* During dive section */}
        {duringDive.map((item, index) => (
          <TimelineItem
            key={`during-${index}`}
            item={item}
            diveDuration={diveDuration}
            samples={samples}
            onPhotoClick={onPhotoClick}
            dotColor="bg-blue-500"
          />
        ))}

        {/* After dive section */}
        {afterDive.length > 0 && (
          <>
            {/* Dive end marker */}
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-green-500 border-2 border-slate-800 shrink-0 z-10" />
              <span className="text-xs text-green-400 font-medium">Dive end</span>
            </div>
            {afterDive.map((item, index) => (
              <TimelineItem
                key={`after-${index}`}
                item={item}
                diveDuration={diveDuration}
                samples={samples}
                onPhotoClick={onPhotoClick}
                dotColor="bg-slate-500"
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
